import { useEffect, useId, isValidElement, Children, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { timArt, TimPose } from '@/lib/timArt'
import { useMagnum, SaysVariant } from '@/context/MagnumContext'

interface MagnumSaysProps {
  children: React.ReactNode
  size?: 'sm' | 'md'
  variant?: SaysVariant
  /** Which portrait to show. Defaults to the variant's natural mood. */
  pose?: TimPose
  className?: string
}

const VARIANT_POSE: Record<SaysVariant, TimPose> = {
  default: 'neutral',
  warning: 'encouraging',
  success: 'happy',
}

/** Flattens a node to plain text, only so we can tell when the message changed. */
function nodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join('')
  if (isValidElement(node)) {
    return nodeText((node.props as { children?: ReactNode }).children)
  }
  return Children.toArray(node).map(nodeText).join('')
}

/**
 * A message in Tim's voice.
 *
 * <p>There is only ever one Tim on screen. Inside the kid shell a
 * MagnumCompanion is already standing there, so rather than drawing a second
 * portrait this hands the message up to him and renders nothing in place — the
 * one Tim reads whatever the screen is doing and says it. On parent pages there
 * is no companion, so it draws the bubble itself.
 */
export default function MagnumSays({ children, size = 'md', variant = 'default', pose, className }: MagnumSaysProps) {
  const { hasCompanion, announce, retract, claimPortrait, portraitOwner, hasProvider } = useMagnum()
  const id = useId()
  const text = nodeText(children)

  useEffect(() => {
    if (!hasCompanion) return
    announce({ id, node: children, variant })
    return () => retract(id)
    // `children` is compared by its flattened text: identical copy, same message.
  }, [hasCompanion, id, text, variant, announce, retract])

  // No companion (parent pages): draw inline, but only one of us gets the face.
  useEffect(() => {
    if (hasCompanion) return
    return claimPortrait(id)
  }, [hasCompanion, id, claimPortrait])

  if (hasCompanion) return null

  // Outside a provider there is no one to coordinate with, so draw the face.
  const showFace = !hasProvider || portraitOwner === id
  const imgSize = size === 'sm' ? 'w-10 h-10' : 'w-14 h-14'
  const borderColor = variant === 'warning'
    ? 'border-red-300'
    : variant === 'success'
      ? 'border-green-300'
      : 'border-primary/30'
  const bgColor = variant === 'warning'
    ? 'bg-red-50'
    : variant === 'success'
      ? 'bg-green-50'
      : 'bg-primary/5'
  const tailColor = variant === 'warning'
    ? 'border-r-red-300'
    : variant === 'success'
      ? 'border-r-green-300'
      : 'border-r-primary/30'
  const tailBg = variant === 'warning'
    ? 'border-r-red-50'
    : variant === 'success'
      ? 'border-r-green-50'
      : 'border-r-primary/5'

  return (
    <div className={cn('flex items-end gap-1', className)}>
      {showFace && (
        <img
          src={timArt(pose ?? VARIANT_POSE[variant])}
          alt="Tim"
          className={cn(imgSize, 'object-contain shrink-0')}
        />
      )}
      <div className="relative">
        <div className={cn(
          'relative border-2 px-4 py-2.5 shadow-sm rounded-2xl',
          showFace && 'rounded-bl-none',
          borderColor, bgColor,
        )}>
          <div className="text-sm">{children}</div>
        </div>
        {showFace && (
          <>
            <div className={cn('absolute bottom-0 -left-[8px] w-0 h-0 border-t-[8px] border-r-[8px] border-t-transparent', tailColor)} />
            <div className={cn('absolute bottom-[2px] -left-[4px] w-0 h-0 border-t-[6px] border-r-[6px] border-t-transparent', tailBg)} />
          </>
        )}
      </div>
    </div>
  )
}
